import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Platform, PopoverController, ModalController, AlertController } from '@ionic/angular/lazy';
import { SupportedCurrencyService } from 'src/app/services/supported-currency.service';
import { PricingTierService } from 'src/app/services/pricing-tier.service';
import { CartService } from 'src/app/services/cart.service';
import { CartAdditionalInfoPage } from 'src/app/modals/cart-additional-info/cart-additional-info.page';
import { CartPickupPage } from 'src/app/modals/cart-pickup/cart-pickup.page';
import { CartShippingPage } from 'src/app/modals/cart-shipping/cart-shipping.page';
import { CartCustomerPage } from 'src/app/modals/cart-customer/cart-customer.page';
import { CartPricingTierPage } from 'src/app/modals/cart-pricing-tier/cart-pricing-tier.page';
import { CartCurrencyPage } from 'src/app/modals/cart-currency/cart-currency.page';
import { CartQuotePage } from 'src/app/modals/cart-quote/cart-quote.page';
import { CartParkPage } from 'src/app/modals/cart-park/cart-park.page';
import { StorageService } from 'src/app/services/storage.service';
import { QuoteService } from 'src/app/services/quote.service';
import { CashDrawerService } from 'src/app/services/cash-drawer.service';
import { CartDiscountPage } from 'src/app/modals/cart-discount/cart-discount.page';
import { Capacitor } from '@capacitor/core';
import { Network } from "@capacitor/network";
import { ObserverService } from 'src/app/services/observer.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
    selector: 'app-cart-options',
    templateUrl: './cart-options.component.html',
    styleUrls: ['./cart-options.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CartOptionsComponent implements OnInit {

    online = true;
    cart = null;
    quote = null;
    pricing_tiers = null;
    supported_currencies = null;
    modal_css_class = '';
    receipt_printer = null;
    desktop_print_client_enabled = false;

    constructor(
        private popoverCtrl: PopoverController,
        private cartService: CartService,
        public modalController: ModalController,
        private pricingTierService: PricingTierService,
        public alertController: AlertController,
        private supportedCurrencyService: SupportedCurrencyService,
        private platform: Platform,
        private storageService: StorageService,
        private observerService: ObserverService,
        private quoteService: QuoteService,
        public permissionService: PermissionService,
        private cashDrawerService: CashDrawerService,
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
        }


        this.platform.ready().then(async () => {

            let network_status = await Network.getStatus();
            this.online = network_status.connected;

            if(Capacitor.isNativePlatform()) {
                this.storageService.get("receipt_printer").then((receipt_printer) => {
                    if (receipt_printer) {
                        this.receipt_printer = receipt_printer;
                    }
                });
            }
            else {
                this.storageService.get("desktop_print_client_enabled").then((desktop_print_client_enabled) => {
                    if(desktop_print_client_enabled) {
                        this.desktop_print_client_enabled = desktop_print_client_enabled;
                    }
                });
            }

        });

        this.cartService.get().then((cart) => {

            this.cart = cart;

            if(cart['quote_id']) {
                this.quoteService.getQuote(cart['quote_id']).then((quote) => {
                    this.quote = quote;
                });
            }

        });

        this.pricingTierService.getPricingTiers().then((pricing_tiers) => {
            this.pricing_tiers = pricing_tiers;
        });

        this.supportedCurrencyService.getSupportedCurrencies().then((supported_currencies) => {
            this.supported_currencies = supported_currencies;
        });

        this.observerService.subscribe('network_status_changed|cart_options_popover', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|cart_options_popover");
    }

    openCashDrawer() {
        this.cashDrawerService.open().then((result) => {
            if(result !== 'opened') {
                this.presentAlert("Please check your printer settings", "");
            }
            this.dismiss();
        });
    }

    clearCart() {
        this.cartService.reset().then(() => {
            this.dismiss();
        });
    }

    async presentCustomerModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartCustomerPage,
            cssClass: this.modal_css_class
        });
        return await modal.present();
    }

    async presentQuoteModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartQuotePage,
            cssClass: this.modal_css_class
        });
        return await modal.present();
    }

    async presentParkCartModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartParkPage,
            cssClass: this.modal_css_class
        });
        return await modal.present();
    }

    async presentAdditionalInfoModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartAdditionalInfoPage,
            cssClass: this.modal_css_class
        });
        return await modal.present();
    }

    async presentPickupModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartPickupPage,
            cssClass: this.modal_css_class
        });
        return await modal.present();
    }

    async presentShippingModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartShippingPage,
            cssClass: this.modal_css_class
        });
        return await modal.present();
    }

    async presentPricingTierModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartPricingTierPage,
        });
        return await modal.present();
    }

    async presentCurrencyModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartCurrencyPage,
        });
        return await modal.present();
    }

    async presentDiscountCartModal() {
        this.dismiss();
        const modal = await this.modalController.create({
            component: CartDiscountPage,
            cssClass: this.modal_css_class
        });
        return await modal.present();
    }

    async dismiss() {
        try {
            await this.popoverCtrl.dismiss();
        } catch (e) {
            // click more than one time popover throws error, so ignore...
        }
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentClearCartConfirmation() {
        let clear_cart_confirmation_message = 'Are you sure you want to clear the cart?';
        if(this.cart.payments.length > 0) {
            clear_cart_confirmation_message += `<br><br>You have <strong>${this.cart.payments.length}</strong> payment${this.cart.payments.length > 1 ? 's' : ''} in your cart which will be discarded.`;
        }
        const alert = await this.alertController.create({
            header: 'Clear cart',
            message: clear_cart_confirmation_message,
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'OK',
                    handler: () => {
                        this.clearCart();
                    }
                }
            ],
        });
        await alert.present();
    }

}
